(function () {
  'use strict';

  var initializedStudios = new WeakSet();

  var SUPPORTED_RASTER_TYPES = [
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/gif',
    'image/bmp'
  ];

  var MAX_FILE_SIZE =
    10 * 1024 * 1024;

  function query(root, selector) {
    return root
      ? root.querySelector(selector)
      : null;
  }

  function queryAll(root, selector) {
    return root
      ? Array.prototype.slice.call(
          root.querySelectorAll(selector)
        )
      : [];
  }

  function setHidden(element, hidden) {
    if (!element) return;

    element.hidden =
      Boolean(hidden);
  }

  function setText(element, value) {
    if (!element) return;

    element.textContent =
      value;
  }

  function setInputValue(element, value) {
    if (!element) return;

    element.value =
      value === null ||
      value === undefined
        ? ''
        : String(value);
  }

  function clamp(value, minimum, maximum) {
    return Math.max(
      minimum,
      Math.min(
        maximum,
        value
      )
    );
  }

  function roundToTwo(value) {
    return Math.round(
      Number(value || 0) * 100
    ) / 100;
  }

  function formatFileSize(bytes) {
    var size =
      Number(bytes || 0);

    if (size < 1024) {
      return size + ' B';
    }

    if (
      size <
      1024 * 1024
    ) {
      return (
        size / 1024
      ).toFixed(1) + ' KB';
    }

    return (
      size /
      (
        1024 *
        1024
      )
    ).toFixed(1) + ' MB';
  }

  function getFileExtension(fileName) {
    var parts =
      String(
        fileName || ''
      ).split('.');

    return parts.length > 1
      ? parts.pop().toLowerCase()
      : '';
  }

  function isSvgFile(file) {
    if (!file) return false;

    return (
      file.type ===
        'image/svg+xml' ||
      getFileExtension(
        file.name
      ) === 'svg'
    );
  }

  function isSupportedRaster(file) {
    if (!file) return false;

    return (
      SUPPORTED_RASTER_TYPES.indexOf(
        file.type
      ) !== -1
    );
  }

  function createObjectUrl(fileOrBlob) {
    try {
      return URL.createObjectURL(
        fileOrBlob
      );
    } catch (error) {
      console.error(
        'Artwork preview URL error:',
        error
      );

      return '';
    }
  }

  function revokeObjectUrl(url) {
    if (!url) return;

    try {
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(
        'Artwork preview cleanup error:',
        error
      );
    }
  }

  function hexToRgb(hex) {
    var clean =
      String(hex || '')
        .replace('#', '');

    if (clean.length === 3) {
      clean =
        clean
          .split('')
          .map(function (character) {
            return character +
              character;
          })
          .join('');
    }

    var parsed =
      parseInt(clean, 16);

    if (
      Number.isNaN(parsed)
    ) {
      return {
        red: 255,
        green: 255,
        blue: 255
      };
    }

    return {
      red:
        (
          parsed >> 16
        ) & 255,

      green:
        (
          parsed >> 8
        ) & 255,

      blue:
        parsed & 255
    };
  }

  function rgbToHex(
    red,
    green,
    blue
  ) {
    function component(value) {
      return clamp(
        Math.round(value),
        0,
        255
      )
        .toString(16)
        .padStart(2, '0');
    }

    return (
      '#' +
      component(red) +
      component(green) +
      component(blue)
    );
  }

  function colorDistance(
    red,
    green,
    blue,
    target
  ) {
    var redDifference =
      red - target.red;

    var greenDifference =
      green - target.green;

    var blueDifference =
      blue - target.blue;

    return Math.sqrt(
      redDifference *
        redDifference +
      greenDifference *
        greenDifference +
      blueDifference *
        blueDifference
    );
  }

  function loadImageFromUrl(url) {
    return new Promise(
      function (resolve, reject) {
        var image =
          new Image();

        image.onload =
          function () {
            resolve(image);
          };

        image.onerror =
          function () {
            reject(
              new Error(
                'The artwork preview could not be opened.'
              )
            );
          };

        image.src =
          url;
      }
    );
  }

  function canvasToBlob(
    canvas,
    type,
    quality
  ) {
    return new Promise(
      function (resolve, reject) {
        canvas.toBlob(
          function (blob) {
            if (!blob) {
              reject(
                new Error(
                  'The prepared artwork file could not be created.'
                )
              );

              return;
            }

            resolve(blob);
          },
          type || 'image/png',
          quality || 0.95
        );
      }
    );
  }

  function getProductForm(studio) {
    var section =
      studio.closest(
        '[id^="ProductSection-"]'
      );

    return section
      ? section.querySelector(
          'form[action*="/cart/add"]'
        )
      : null;
  }

  function movePropertiesIntoProductForm(
    studio
  ) {
    var form =
      getProductForm(studio);

    if (!form) return;

    if (!form.id) {
      form.id =
        'TwistedSwagProductForm-' +
        Math.random()
          .toString(36)
          .slice(2);
    }

    queryAll(
      studio,
      '.ts-artwork-card__order-properties input'
    ).forEach(
      function (input) {
        /*
          Keep each artwork property input inside its original card
          so the uploader, analyzer, background remover, vectorizer,
          and version selector can continue updating the correct field.

          The HTML form attribute connects the input to Shopify's
          product form without physically moving it out of the card.
        */
        input.setAttribute(
          'form',
          form.id
        );
      }
    );
  }

  function initializeStudio(studio) {
    if (
      !studio ||
      initializedStudios.has(
        studio
      )
    ) {
      return;
    }

    initializedStudios.add(
      studio
    );

    var workerUrl =
      String(
        studio.getAttribute(
          'data-worker-url'
        ) || ''
      ).replace(
        /\/vectorize\/?$/,
        ''
      );

    var vectorEndpoint =
      workerUrl +
      '/vectorize';

    var storageEndpoint =
      workerUrl +
      '/store-file';

    var cards =
      queryAll(
        studio,
        '[data-ts-artwork-card]'
      );

    var studioStatus =
      query(
        studio,
        '[data-ts-studio-status]'
      );

    var fileCount =
      query(
        studio,
        '[data-ts-file-count]'
      );

    var choiceCount =
      query(
        studio,
        '[data-ts-choice-count]'
      );

    var modal =
      document.querySelector(
        '[data-ts-studio-modal]'
      );

    var modalImage =
      query(
        modal,
        '[data-ts-modal-image]'
      );

    var modalTitle =
      query(
        modal,
        '[data-ts-modal-title]'
      );

    var modalClose =
      query(
        modal,
        '[data-ts-modal-close]'
      );

    var previousFocusedElement =
      null;

    var states = cards.map(
      function (card) {
        return {
          card:
            card,

          number:
            Number(
              card.getAttribute(
                'data-artwork-number'
              ) || 1
            ),

          file:
            null,

          originalUrl:
            '',

          currentPreviewUrl:
            '',

          backgroundUrl:
            '',

          vectorUrl:
            '',

          originalStoredUrl:
            '',

          backgroundStoredUrl:
            '',

          naturalWidth:
            0,

          naturalHeight:
            0,

          aspectRatio:
            1,

          isVector:
            false,

          isLocked:
            true,

          selectedVersion:
            'original',

          backgroundBlob:
            null,

          analysis:
            null,

          progressTimer:
            null
        };
      }
    );

    function getStateFromElement(element) {
      var card =
        element.closest(
          '[data-ts-artwork-card]'
        );

      if (!card) return null;

      return states.find(
        function (state) {
          return (
            state.card ===
            card
          );
        }
      ) || null;
    }

    function closeAllTooltips(
      exceptButton
    ) {
      queryAll(
        studio,
        '[data-ts-tooltip-button]'
      ).forEach(
        function (button) {
          if (
            exceptButton &&
            button ===
              exceptButton
          ) {
            return;
          }

          button.setAttribute(
            'aria-expanded',
            'false'
          );

          var tooltip =
            button.parentElement
              ? query(
                  button.parentElement,
                  '[data-ts-tooltip]'
                )
              : null;

          setHidden(
            tooltip,
            true
          );
        }
      );
    }

    function toggleTooltip(button) {
      var tooltip =
        button.parentElement
          ? query(
              button.parentElement,
              '[data-ts-tooltip]'
            )
          : null;

      if (!tooltip) return;

      var isOpening =
        button.getAttribute(
          'aria-expanded'
        ) !== 'true';

      closeAllTooltips(
        isOpening
          ? button
          : null
      );

      button.setAttribute(
        'aria-expanded',
        isOpening
          ? 'true'
          : 'false'
      );

      setHidden(
        tooltip,
        !isOpening
      );
    }

    function openModal(
      imageUrl,
      title
    ) {
      if (
        !modal ||
        !modalImage ||
        !imageUrl
      ) {
        return;
      }

      previousFocusedElement =
        document.activeElement;

      modalImage.src =
        imageUrl;

      modalImage.alt =
        title ||
        'Artwork preview';

      setText(
        modalTitle,
        title ||
        'Artwork Preview'
      );

      modal.classList.add(
        'is-open'
      );

      modal.setAttribute(
        'aria-hidden',
        'false'
      );

      document.body.classList.add(
        'ts-studio-modal-open'
      );

      if (modalClose) {
        modalClose.focus();
      }
    }

    function closeModal() {
      if (!modal) return;

      modal.classList.remove(
        'is-open'
      );

      modal.setAttribute(
        'aria-hidden',
        'true'
      );

      document.body.classList.remove(
        'ts-studio-modal-open'
      );

      if (modalImage) {
        modalImage.removeAttribute(
          'src'
        );
      }

      if (
        previousFocusedElement &&
        typeof previousFocusedElement.focus ===
          'function'
      ) {
        previousFocusedElement.focus();
      }
    }

    function activateNextCard(state) {
      var nextState =
        states[state.number];

      if (
        nextState &&
        !nextState.card.classList.contains(
          'is-active'
        )
      ) {
        nextState.card.classList.add(
          'is-active'
        );
      }
    }

    function updateSummary() {
      var populated =
        states.filter(
          function (state) {
            return Boolean(
              state.file
            );
          }
        );

      var completedChoices =
        populated.filter(
          function (state) {
            return Boolean(
              state.selectedVersion
            );
          }
        );

      setText(
        fileCount,
        populated.length +
          ' of 8'
      );

      setText(
        choiceCount,
        String(
          completedChoices.length
        )
      );

      if (
        populated.length === 0
      ) {
        setText(
          studioStatus,
          'Waiting for artwork'
        );
      } else if (
        populated.length === 1
      ) {
        setText(
          studioStatus,
          '1 artwork file ready'
        );
      } else {
        setText(
          studioStatus,
          populated.length +
            ' artwork files ready'
        );
      }
    }

    function clearProgressTimer(state) {
      if (
        state.progressTimer
      ) {
        window.clearInterval(
          state.progressTimer
        );

        state.progressTimer =
          null;
      }
    }

    function resetStateUrls(state) {
      revokeObjectUrl(
        state.originalUrl
      );

      if (
        state.currentPreviewUrl &&
        state.currentPreviewUrl !==
          state.originalUrl
      ) {
        revokeObjectUrl(
          state.currentPreviewUrl
        );
      }

      if (
        state.backgroundUrl &&
        state.backgroundUrl !==
          state.originalUrl &&
        state.backgroundUrl !==
          state.currentPreviewUrl
      ) {
        revokeObjectUrl(
          state.backgroundUrl
        );
      }

      state.originalUrl =
        '';

      state.currentPreviewUrl =
        '';

      state.backgroundUrl =
        '';
    }

    function clearCardUi(state) {
      var card =
        state.card;

      clearProgressTimer(
        state
      );

      resetStateUrls(
        state
      );

      state.file =
        null;

      state.backgroundBlob =
        null;

      state.vectorUrl =
        '';

      state.originalStoredUrl =
        '';

      state.backgroundStoredUrl =
        '';

      state.naturalWidth =
        0;

      state.naturalHeight =
        0;

      state.aspectRatio =
        1;

      state.isVector =
        false;

      state.isLocked =
        true;

      state.selectedVersion =
        'original';

      state.analysis =
        null;

      card.classList.remove(
        'has-file'
      );

      var input =
        query(
          card,
          '[data-ts-artwork-input]'
        );

      if (input) {
        input.value =
          '';
      }

      setHidden(
        query(
          card,
          '[data-ts-artwork-workspace]'
        ),
        true
      );

      setHidden(
        query(
          card,
          '[data-ts-upload-area]'
        ),
        false
      );

      setText(
        query(
          card,
          '[data-ts-file-status]'
        ),
        'No file selected'
      );

      setText(
        query(
          card,
          '[data-ts-artwork-title]'
        ),
        state.number === 1
          ? 'Choose your first artwork file'
          : 'Additional artwork'
      );

      setText(
        query(
          card,
          '[data-ts-upload-button-text]'
        ),
        'Choose Artwork ' +
          state.number
      );

      [
        '[data-ts-artwork-preview-image]',
        '[data-ts-version-original-image]',
        '[data-ts-version-background-image]',
        '[data-ts-version-vector-image]'
      ].forEach(
        function (selector) {
          var image =
            query(
              card,
              selector
            );

          if (image) {
            image.removeAttribute(
              'src'
            );
          }
        }
      );

      setText(
        query(
          card,
          '[data-ts-file-type]'
        ),
        '—'
      );

      setText(
        query(
          card,
          '[data-ts-file-size]'
        ),
        '—'
      );

      setText(
        query(
          card,
          '[data-ts-pixel-dimensions]'
        ),
        '—'
      );

      setText(
        query(
          card,
          '[data-ts-transparency-status]'
        ),
        '—'
      );

      setText(
        query(
          card,
          '[data-ts-dominant-colors]'
        ),
        '—'
      );

      setText(
        query(
          card,
          '[data-ts-printable-colors]'
        ),
        '—'
      );

      setInputValue(
        query(
          card,
          '[data-ts-print-width]'
        ),
        ''
      );

      setInputValue(
        query(
          card,
          '[data-ts-print-height]'
        ),
        ''
      );

      var locationSelect =
        query(
          card,
          '[data-ts-location-select]'
        );

      if (locationSelect) {
        locationSelect.value =
          '';
      }

      var lockButton =
        query(
          card,
          '[data-ts-aspect-lock]'
        );

      if (lockButton) {
        lockButton.classList.add(
          'is-locked'
        );

        lockButton.setAttribute(
          'aria-pressed',
          'true'
        );
      }

      setText(
        query(
          card,
          '[data-ts-aspect-lock-text]'
        ),
        'Locked'
      );

      setText(
        query(
          card,
          '[data-ts-effective-dpi]'
        ),
        '—'
      );

      setText(
        query(
          card,
          '[data-ts-maximum-size]'
        ),
        '—'
      );

      updateQuality(
        state,
        'waiting',
        'Waiting',
        '',
        ''
      );

      setHidden(
        query(
          card,
          '[data-ts-version-option="background_removed"]'
        ),
        true
      );

      setHidden(
        query(
          card,
          '[data-ts-version-option="vector"]'
        ),
        true
      );

      setHidden(
        query(
          card,
          '[data-ts-vector-progress]'
        ),
        true
      );

      setHidden(
        query(
          card,
          '[data-ts-vector-error]'
        ),
        true
      );

      var restoreButton =
        query(
          card,
          '[data-ts-restore-original]'
        );

      if (restoreButton) {
        restoreButton.disabled =
          true;
      }

      selectVersion(
        state,
        'original'
      );

      clearProperties(
        state
      );

      updateSummary();
    }

    function clearProperties(state) {
      var card =
        state.card;

      [
        '[data-ts-property-original-file]',
        '[data-ts-property-production-file]',
        '[data-ts-property-location]',
        '[data-ts-property-width]',
        '[data-ts-property-height]',
        '[data-ts-property-dpi]',
        '[data-ts-property-quality]',
        '[data-ts-property-vector-url]'
      ].forEach(
        function (selector) {
          setInputValue(
            query(
              card,
              selector
            ),
            ''
          );
        }
      );

      setInputValue(
        query(
          card,
          '[data-ts-property-version]'
        ),
        'Original Artwork'
      );

      setInputValue(
        query(
          card,
          '[data-ts-property-background-status]'
        ),
        'No'
      );
    }

    async function uploadStoredFile(
      state,
      blobOrFile,
      versionType,
      fileName
    ) {
      var formData =
        new FormData();

      var resolvedName =
        fileName ||
        (
          state.file
            ? state.file.name
            : 'artwork.png'
        );

      formData.append(
        'file',
        blobOrFile,
        resolvedName
      );

      formData.append(
        'artworkNumber',
        String(
          state.number
        )
      );

      formData.append(
        'versionType',
        versionType
      );

      formData.append(
        'originalFileName',
        state.file
          ? state.file.name
          : resolvedName
      );

      var response =
        await fetch(
          storageEndpoint,
          {
            method:
              'POST',

            body:
              formData,

            credentials:
              'omit'
          }
        );

      var data;

      try {
        data =
          await response.json();
      } catch (error) {
        throw new Error(
          'Twisted Swag received an unreadable storage response.'
        );
      }

      if (
        !response.ok ||
        !data ||
        data.success !==
          true ||
        !data.fileUrl
      ) {
        throw new Error(
          data &&
          data.error
            ? data.error
            : 'The prepared artwork could not be saved.'
        );
      }

      return data.fileUrl;
    }

    async function storeOriginalFile(state) {
      if (
        !state.file ||
        state.originalStoredUrl
      ) {
        return;
      }

      try {
        state.originalStoredUrl =
          await uploadStoredFile(
            state,
            state.file,
            'original',
            state.file.name
          );

        setInputValue(
          query(
            state.card,
            '[data-ts-property-original-file]'
          ),
          state.originalStoredUrl
        );

        if (
          state.selectedVersion ===
          'original'
        ) {
          setInputValue(
            query(
              state.card,
              '[data-ts-property-production-file]'
            ),
            state.originalStoredUrl
          );
        }
      } catch (error) {
        console.error(
          'Original artwork storage error:',
          error
        );
      }
    }

    function selectVersion(
      state,
      version
    ) {
      var card =
        state.card;

      state.selectedVersion =
        version;

      queryAll(
        card,
        '[data-ts-version-option]'
      ).forEach(
        function (option) {
          var optionVersion =
            option.getAttribute(
              'data-ts-version-option'
            );

          option.classList.toggle(
            'is-selected',
            optionVersion ===
              version
          );

          var input =
            query(
              option,
              '[data-ts-version-choice]'
            );

          if (input) {
            input.checked =
              optionVersion ===
              version;
          }
        }
      );

      var versionLabel =
        'Original Artwork';

      var productionUrl =
        state.originalStoredUrl ||
        '';

      if (
        version ===
        'background_removed'
      ) {
        versionLabel =
          'Background Removed';

        productionUrl =
          state.backgroundStoredUrl ||
          '';
      }

      if (
        version ===
        'vector'
      ) {
        versionLabel =
          'Enhanced Vector';

        productionUrl =
          state.vectorUrl ||
          '';
      }

      setText(
        query(
          card,
          '[data-ts-selected-version]'
        ),
        versionLabel
      );

      setInputValue(
        query(
          card,
          '[data-ts-property-version]'
        ),
        versionLabel
      );

      setInputValue(
        query(
          card,
          '[data-ts-property-production-file]'
        ),
        productionUrl
      );

      updateSummary();
    }

    function updateQuality(
      state,
      level,
      label,
      title,
      description
    ) {
      var card =
        state.card;

      var pill =
        query(
          card,
          '[data-ts-quality-pill]'
        );

      var message =
        query(
          card,
          '[data-ts-quality-message]'
        );

      if (pill) {
        pill.classList.remove(
          'is-success',
          'is-info',
          'is-warning',
          'is-danger'
        );

        if (
          level ===
          'success'
        ) {
          pill.classList.add(
            'is-success'
          );
        } else if (
          level ===
          'info'
        ) {
          pill.classList.add(
            'is-info'
          );
        } else if (
          level ===
          'warning'
        ) {
          pill.classList.add(
            'is-warning'
          );
        } else if (
          level ===
          'danger'
        ) {
          pill.classList.add(
            'is-danger'
          );
        }

        pill.textContent =
          label;
      }

      if (message) {
        message.classList.remove(
          'is-success',
          'is-info'
        );

        if (
          level ===
          'success'
        ) {
          message.classList.add(
            'is-success'
          );
        }

        if (
          level ===
          'info'
        ) {
          message.classList.add(
            'is-info'
          );
        }

        setHidden(
          message,
          !title
        );
      }

      setText(
        query(
          card,
          '[data-ts-quality-title]'
        ),
        title
      );

      setText(
        query(
          card,
          '[data-ts-quality-description]'
        ),
        description
      );

      setInputValue(
        query(
          card,
          '[data-ts-property-quality]'
        ),
        label
      );
    }

    function updateSizeAndQuality(state) {
      var card =
        state.card;

      var widthInput =
        query(
          card,
          '[data-ts-print-width]'
        );

      var heightInput =
        query(
          card,
          '[data-ts-print-height]'
        );

      var width =
        parseFloat(
          widthInput
            ? widthInput.value
            : ''
        );

      var height =
        parseFloat(
          heightInput
            ? heightInput.value
            : ''
        );

      if (
        !width ||
        !height ||
        width <= 0 ||
        height <= 0
      ) {
        setText(
          query(
            card,
            '[data-ts-effective-dpi]'
          ),
          '—'
        );

        updateQuality(
          state,
          'waiting',
          'Waiting',
          '',
          ''
        );

        return;
      }

      var dpi;

      if (
        state.isVector
      ) {
        dpi =
          Infinity;
      } else {
        dpi =
          Math.min(
            state.naturalWidth /
              width,
            state.naturalHeight /
              height
          );
      }

      setText(
        query(
          card,
          '[data-ts-effective-dpi]'
        ),
        state.isVector
          ? 'Scalable'
          : Math.round(dpi) +
            ' DPI'
      );

      setInputValue(
        query(
          card,
          '[data-ts-property-width]'
        ),
        roundToTwo(width) +
          ' in'
      );

      setInputValue(
        query(
          card,
          '[data-ts-property-height]'
        ),
        roundToTwo(height) +
          ' in'
      );

      setInputValue(
        query(
          card,
          '[data-ts-property-dpi]'
        ),
        state.isVector
          ? 'Vector / Scalable'
          : Math.round(dpi) +
            ' DPI'
      );

      var locationSelect =
        query(
          card,
          '[data-ts-location-select]'
        );

      var selectedOption =
        locationSelect &&
        locationSelect.selectedIndex >= 0
          ? locationSelect.options[
              locationSelect.selectedIndex
            ]
          : null;

      var maxWidth =
        selectedOption
          ? parseFloat(
              selectedOption.getAttribute(
                'data-max-width'
              )
            )
          : 0;

      var maxHeight =
        selectedOption
          ? parseFloat(
              selectedOption.getAttribute(
                'data-max-height'
              )
            )
          : 0;

      var exceedsLocation =
        Boolean(
          (
            maxWidth &&
            width >
              maxWidth
          ) ||
          (
            maxHeight &&
            height >
              maxHeight
          )
        );

      if (
        exceedsLocation
      ) {
        updateQuality(
          state,
          'warning',
          'Exceeds location',
          'Selected print size is too large',
          'Reduce the width or height to fit the standard maximum for the selected imprint location.'
        );

        return;
      }

      if (
        state.isVector
      ) {
        updateQuality(
          state,
          'success',
          'Vector artwork',
          'Ready for print production',
          'Vector artwork can scale cleanly without losing edge sharpness.'
        );

        return;
      }

      if (
        dpi >= 250
      ) {
        updateQuality(
          state,
          'success',
          'Print-ready resolution',
          'Ready for print production',
          'This artwork has sufficient resolution for the entered print size.'
        );
      } else if (
        dpi >= 150
      ) {
        updateQuality(
          state,
          'info',
          'Usable resolution',
          'This artwork may be usable',
          'A larger or vector file may produce a sharper result, especially for fine details.'
        );
      } else if (
        dpi >= 100
      ) {
        updateQuality(
          state,
          'warning',
          'Low resolution',
          'Higher-resolution artwork recommended',
          'This artwork may lose sharpness at the entered print size.'
        );
      } else {
        updateQuality(
          state,
          'danger',
          'Very low resolution',
          'This artwork may print blurry',
          'This file may appear pixelated at the entered print size. Upload a larger image or create an enhanced vector.'
        );
      }
    }

    function setDefaultPrintSize(state) {
      var card =
        state.card;

      var widthInput =
        query(
          card,
          '[data-ts-print-width]'
        );

      var heightInput =
        query(
          card,
          '[data-ts-print-height]'
        );

      if (
        !state.naturalWidth ||
        !state.naturalHeight
      ) {
        return;
      }

      var maximumWidth =
        11;

      var maximumHeight =
        14;

      var scale =
        Math.min(
          maximumWidth /
            state.naturalWidth,
          maximumHeight /
            state.naturalHeight
        );

      var width =
        state.naturalWidth *
        scale;

      var height =
        state.naturalHeight *
        scale;

      setInputValue(
        widthInput,
        roundToTwo(width)
      );

      setInputValue(
        heightInput,
        roundToTwo(height)
      );

      setText(
        query(
          card,
          '[data-ts-maximum-size]'
        ),
        state.isVector
          ? 'Scalable'
          : roundToTwo(
              state.naturalWidth /
              150
            ) +
            ' × ' +
            roundToTwo(
              state.naturalHeight /
              150
            ) +
            ' in'
      );

      updateSizeAndQuality(
        state
      );
    }

    function analyzeImageData(
      imageData
    ) {
      var data =
        imageData.data;

      var pixels =
        0;

      var transparentPixels =
        0;

      var clusters =
        [];

      var step =
        Math.max(
          1,
          Math.floor(
            Math.sqrt(
              (
                imageData.width *
                imageData.height
              ) /
              18000
            )
          )
        );

      function addCluster(
        red,
        green,
        blue
      ) {
        var match =
          null;

        var smallestDistance =
          Infinity;

        clusters.forEach(
          function (cluster) {
            var distance =
              colorDistance(
                red,
                green,
                blue,
                cluster
              );

            if (
              distance <
              smallestDistance
            ) {
              smallestDistance =
                distance;

              match =
                cluster;
            }
          }
        );

        if (
          match &&
          smallestDistance <= 70
        ) {
          var newCount =
            match.count + 1;

          match.red =
            (
              match.red *
                match.count +
              red
            ) / newCount;

          match.green =
            (
              match.green *
                match.count +
              green
            ) / newCount;

          match.blue =
            (
              match.blue *
                match.count +
              blue
            ) / newCount;

          match.count =
            newCount;
        } else {
          clusters.push({
            red:
              red,

            green:
              green,

            blue:
              blue,

            count:
              1
          });
        }
      }

      for (
        var y = 0;
        y < imageData.height;
        y += step
      ) {
        for (
          var x = 0;
          x < imageData.width;
          x += step
        ) {
          var index =
            (
              y *
              imageData.width +
              x
            ) * 4;

          var alpha =
            data[index + 3];

          if (
            alpha < 245
          ) {
            transparentPixels +=
              1;
          }

          if (
            alpha < 70
          ) {
            continue;
          }

          pixels +=
            1;

          addCluster(
            data[index],
            data[index + 1],
            data[index + 2]
          );
        }
      }

      var minimumCluster =
        Math.max(
          4,
          Math.floor(
            pixels *
            0.018
          )
        );

      var dominant =
        clusters
          .filter(
            function (cluster) {
              return (
                cluster.count >=
                minimumCluster
              );
            }
          )
          .sort(
            function (
              first,
              second
            ) {
              return (
                second.count -
                first.count
              );
            }
          );

      return {
        dominantColors:
          Math.max(
            1,
            dominant.length
          ),

        printableColors:
          dominant.length > 8
            ? 9
            : Math.max(
                1,
                dominant.length
              ),

        hasTransparency:
          transparentPixels > 0
      };
    }

    async function analyzeFile(state) {
      var card =
        state.card;

      if (
        state.isVector
      ) {
        state.naturalWidth =
          1000;

        state.naturalHeight =
          1000;

        state.aspectRatio =
          1;

        state.analysis = {
          dominantColors:
            1,

          printableColors:
            1,

          hasTransparency:
            true
        };

        setText(
          query(
            card,
            '[data-ts-pixel-dimensions]'
          ),
          'Vector artwork'
        );

        setText(
          query(
            card,
            '[data-ts-transparency-status]'
          ),
          'Supported'
        );

        setText(
          query(
            card,
            '[data-ts-dominant-colors]'
          ),
          'Vector artwork'
        );

        setText(
          query(
            card,
            '[data-ts-printable-colors]'
          ),
          'Production review'
        );

        setDefaultPrintSize(
          state
        );

        return;
      }

      var image =
        await loadImageFromUrl(
          state.originalUrl
        );

      state.naturalWidth =
        image.naturalWidth;

      state.naturalHeight =
        image.naturalHeight;

      state.aspectRatio =
        state.naturalWidth /
        state.naturalHeight;

      var maximumSize =
        300;

      var scale =
        Math.min(
          1,
          maximumSize /
            Math.max(
              state.naturalWidth,
              state.naturalHeight
            )
        );

      var canvas =
        document.createElement(
          'canvas'
        );

      canvas.width =
        Math.max(
          1,
          Math.round(
            state.naturalWidth *
            scale
          )
        );

      canvas.height =
        Math.max(
          1,
          Math.round(
            state.naturalHeight *
            scale
          )
        );

      var context =
        canvas.getContext(
          '2d',
          {
            willReadFrequently:
              true
          }
        );

      context.drawImage(
        image,
        0,
        0,
        canvas.width,
        canvas.height
      );

      var imageData =
        context.getImageData(
          0,
          0,
          canvas.width,
          canvas.height
        );

      state.analysis =
        analyzeImageData(
          imageData
        );

      setText(
        query(
          card,
          '[data-ts-pixel-dimensions]'
        ),
        state.naturalWidth +
          ' × ' +
          state.naturalHeight +
          ' px'
      );

      setText(
        query(
          card,
          '[data-ts-transparency-status]'
        ),
        state.analysis.hasTransparency
          ? 'Detected'
          : 'Solid background'
      );

      setText(
        query(
          card,
          '[data-ts-dominant-colors]'
        ),
        state.analysis.dominantColors > 8
          ? 'Full color / blended'
          : state.analysis.dominantColors +
            (
              state.analysis.dominantColors === 1
                ? ' color'
                : ' colors'
            )
      );

      setText(
        query(
          card,
          '[data-ts-printable-colors]'
        ),
        state.analysis.printableColors > 8
          ? 'Full color'
          : state.analysis.printableColors +
            (
              state.analysis.printableColors === 1
                ? ' color'
                : ' colors'
            )
      );

      setDefaultPrintSize(
        state
      );
    }

    async function handleFileSelection(
      state,
      file
    ) {
      var card =
        state.card;

      if (!file) return;

      if (
        file.size >
        MAX_FILE_SIZE
      ) {
        window.alert(
          'This artwork file is larger than the 10 MB limit.'
        );

        return;
      }

      if (
        !isSvgFile(file) &&
        !isSupportedRaster(file)
      ) {
        window.alert(
          'Choose a PNG, JPG, JPEG, WebP, GIF, BMP, or SVG artwork file.'
        );

        return;
      }

      resetStateUrls(
        state
      );

      state.file =
        file;

      state.isVector =
        isSvgFile(
          file
        );

      state.originalUrl =
        createObjectUrl(
          file
        );

      state.currentPreviewUrl =
        state.originalUrl;

      state.backgroundUrl =
        '';

      state.vectorUrl =
        '';

      state.originalStoredUrl =
        '';

      state.backgroundStoredUrl =
        '';

      state.backgroundBlob =
        null;

      state.selectedVersion =
        'original';

      card.classList.add(
        'has-file'
      );

      setHidden(
        query(
          card,
          '[data-ts-upload-area]'
        ),
        true
      );

      setHidden(
        query(
          card,
          '[data-ts-artwork-workspace]'
        ),
        false
      );

      setText(
        query(
          card,
          '[data-ts-artwork-title]'
        ),
        file.name
      );

      setText(
        query(
          card,
          '[data-ts-file-status]'
        ),
        'File selected'
      );

      setText(
        query(
          card,
          '[data-ts-file-type]'
        ),
        state.isVector
          ? 'SVG'
          : (
              file.type ||
              getFileExtension(
                file.name
              ).toUpperCase()
            )
      );

      setText(
        query(
          card,
          '[data-ts-file-size]'
        ),
        formatFileSize(
          file.size
        )
      );

      [
        '[data-ts-artwork-preview-image]',
        '[data-ts-version-original-image]'
      ].forEach(
        function (selector) {
          var image =
            query(
              card,
              selector
            );

          if (image) {
            image.src =
              state.originalUrl;
          }
        }
      );

      setHidden(
        query(
          card,
          '[data-ts-version-option="background_removed"]'
        ),
        true
      );

      setHidden(
        query(
          card,
          '[data-ts-version-option="vector"]'
        ),
        true
      );

      var vectorButton =
        query(
          card,
          '[data-ts-create-vector]'
        );

      if (vectorButton) {
        vectorButton.disabled =
          state.isVector;

        setText(
          query(
            card,
            '[data-ts-create-vector-text]'
          ),
          state.isVector
            ? 'Artwork Is Already Vector'
            : 'Create Enhanced Vector'
        );
      }

      selectVersion(
        state,
        'original'
      );

      activateNextCard(
        state
      );

      updateSummary();

      try {
        await analyzeFile(
          state
        );
      } catch (error) {
        console.error(
          'Artwork analysis error:',
          error
        );
      }

      storeOriginalFile(
        state
      );
    }

    async function detectBackgroundColor(
      state
    ) {
      if (
        !state.file ||
        state.isVector
      ) {
        return;
      }

      try {
        var image =
          await loadImageFromUrl(
            state.originalUrl
          );

        var canvas =
          document.createElement(
            'canvas'
          );

        canvas.width =
          image.naturalWidth;

        canvas.height =
          image.naturalHeight;

        var context =
          canvas.getContext(
            '2d',
            {
              willReadFrequently:
                true
            }
          );

        context.drawImage(
          image,
          0,
          0
        );

        var points = [
          [0, 0],
          [canvas.width - 1, 0],
          [0, canvas.height - 1],
          [canvas.width - 1, canvas.height - 1]
        ];

        var red =
          0;

        var green =
          0;

        var blue =
          0;

        points.forEach(
          function (point) {
            var pixel =
              context.getImageData(
                point[0],
                point[1],
                1,
                1
              ).data;

            red +=
              pixel[0];

            green +=
              pixel[1];

            blue +=
              pixel[2];
          }
        );

        var detected =
          rgbToHex(
            red /
              points.length,
            green /
              points.length,
            blue /
              points.length
          );

        setInputValue(
          query(
            state.card,
            '[data-ts-background-color]'
          ),
          detected
        );
      } catch (error) {
        console.error(
          'Background detection error:',
          error
        );
      }
    }

    async function applyBackgroundRemoval(
      state
    ) {
      if (
        !state.file ||
        state.isVector
      ) {
        window.alert(
          'Background removal is available for PNG, JPG, JPEG, WebP, GIF, and BMP files.'
        );

        return;
      }

      var card =
        state.card;

      var colorInput =
        query(
          card,
          '[data-ts-background-color]'
        );

      var toleranceInput =
        query(
          card,
          '[data-ts-background-tolerance]'
        );

      var cleanupInput =
        query(
          card,
          '[data-ts-edge-cleanup]'
        );

      var target =
        hexToRgb(
          colorInput
            ? colorInput.value
            : '#ffffff'
        );

      var tolerance =
        Number(
          toleranceInput
            ? toleranceInput.value
            : 32
        );

      var cleanup =
        Number(
          cleanupInput
            ? cleanupInput.value
            : 1
        );

      try {
        var image =
          await loadImageFromUrl(
            state.originalUrl
          );

        var canvas =
          document.createElement(
            'canvas'
          );

        canvas.width =
          image.naturalWidth;

        canvas.height =
          image.naturalHeight;

        var context =
          canvas.getContext(
            '2d',
            {
              willReadFrequently:
                true
            }
          );

        context.drawImage(
          image,
          0,
          0
        );

        var imageData =
          context.getImageData(
            0,
            0,
            canvas.width,
            canvas.height
          );

        var data =
          imageData.data;

        for (
          var index = 0;
          index < data.length;
          index += 4
        ) {
          var distance =
            colorDistance(
              data[index],
              data[index + 1],
              data[index + 2],
              target
            );

          if (
            distance <=
            tolerance
          ) {
            data[index + 3] =
              0;
          } else if (
            cleanup > 0 &&
            distance <=
              tolerance +
              cleanup * 12
          ) {
            var fadeRange =
              cleanup * 12;

            var fade =
              (
                distance -
                tolerance
              ) /
              fadeRange;

            data[index + 3] =
              Math.round(
                data[index + 3] *
                clamp(
                  fade,
                  0,
                  1
                )
              );
          }
        }

        context.putImageData(
          imageData,
          0,
          0
        );

        var blob =
          await canvasToBlob(
            canvas,
            'image/png',
            1
          );

        revokeObjectUrl(
          state.backgroundUrl
        );

        state.backgroundBlob =
          blob;

        state.backgroundUrl =
          createObjectUrl(
            blob
          );

        state.currentPreviewUrl =
          state.backgroundUrl;

        var mainPreview =
          query(
            card,
            '[data-ts-artwork-preview-image]'
          );

        var backgroundPreview =
          query(
            card,
            '[data-ts-version-background-image]'
          );

        if (mainPreview) {
          mainPreview.src =
            state.backgroundUrl;
        }

        if (backgroundPreview) {
          backgroundPreview.src =
            state.backgroundUrl;
        }

        setHidden(
          query(
            card,
            '[data-ts-version-option="background_removed"]'
          ),
          false
        );

        var restoreButton =
          query(
            card,
            '[data-ts-restore-original]'
          );

        if (restoreButton) {
          restoreButton.disabled =
            false;
        }

        setInputValue(
          query(
            card,
            '[data-ts-property-background-status]'
          ),
          'Yes'
        );

        state.backgroundStoredUrl =
          await uploadStoredFile(
            state,
            blob,
            'background-removed',
            'artwork-' +
              state.number +
              '-background-removed.png'
          );

        selectVersion(
          state,
          'background_removed'
        );
      } catch (error) {
        console.error(
          'Background removal error:',
          error
        );

        window.alert(
          error &&
          error.message
            ? error.message
            : 'The background could not be removed. Your original artwork remains safe.'
        );
      }
    }

    function restoreOriginal(state) {
      if (!state.file) return;

      state.currentPreviewUrl =
        state.originalUrl;

      var preview =
        query(
          state.card,
          '[data-ts-artwork-preview-image]'
        );

      if (preview) {
        preview.src =
          state.originalUrl;
      }

      selectVersion(
        state,
        'original'
      );
    }

    function updateVectorProgress(
      state,
      percent,
      message
    ) {
      var card =
        state.card;

      var progress =
        query(
          card,
          '[data-ts-vector-progress]'
        );

      setHidden(
        progress,
        false
      );

      setText(
        query(
          card,
          '[data-ts-vector-progress-message]'
        ),
        message
      );

      setText(
        query(
          card,
          '[data-ts-vector-progress-percent]'
        ),
        Math.round(
          percent
        ) + '%'
      );

      var fill =
        query(
          card,
          '[data-ts-vector-progress-fill]'
        );

      if (fill) {
        fill.style.width =
          clamp(
            percent,
            0,
            100
          ) + '%';
      }
    }

    function startVectorProgress(state) {
      clearProgressTimer(
        state
      );

      var percent =
        8;

      updateVectorProgress(
        state,
        percent,
        'Preparing artwork…'
      );

      state.progressTimer =
        window.setInterval(
          function () {
            if (
              percent >= 88
            ) {
              return;
            }

            percent +=
              percent < 40
                ? 5
                : (
                    percent < 70
                      ? 3
                      : 1
                  );

            var message =
              'Preparing artwork…';

            if (
              percent >= 35 &&
              percent < 65
            ) {
              message =
                'Cleaning edges and shapes…';
            } else if (
              percent >= 65 &&
              percent < 82
            ) {
              message =
                'Building the scalable vector…';
            } else if (
              percent >= 82
            ) {
              message =
                'Saving the enhanced artwork…';
            }

            updateVectorProgress(
              state,
              percent,
              message
            );
          },
          350
        );
    }

    async function createVector(state) {
      if (
        !state.file ||
        state.isVector
      ) {
        return;
      }

      var card =
        state.card;

      var button =
        query(
          card,
          '[data-ts-create-vector]'
        );

      var buttonText =
        query(
          card,
          '[data-ts-create-vector-text]'
        );

      setHidden(
        query(
          card,
          '[data-ts-vector-error]'
        ),
        true
      );

      if (button) {
        button.disabled =
          true;
      }

      setText(
        buttonText,
        'Creating Enhanced Vector…'
      );

      startVectorProgress(
        state
      );

      var formData =
        new FormData();

      var sourceFile =
        state.backgroundBlob ||
        state.file;

      var sourceName =
        state.backgroundBlob
          ? 'artwork-' +
            state.number +
            '-transparent.png'
          : state.file.name;

      formData.append(
        'image',
        sourceFile,
        sourceName
      );

      try {
        var response =
          await fetch(
            vectorEndpoint,
            {
              method:
                'POST',

              body:
                formData,

              credentials:
                'omit'
            }
          );

        var data =
          await response.json();

        if (
          !response.ok ||
          !data ||
          data.success !==
            true ||
          !data.vectorUrl
        ) {
          throw new Error(
            data &&
            data.error
              ? data.error
              : 'The enhanced vector could not be created.'
          );
        }

        clearProgressTimer(
          state
        );

        updateVectorProgress(
          state,
          100,
          'Enhanced vector ready.'
        );

        state.vectorUrl =
          data.vectorUrl;

        var vectorPreview =
          query(
            card,
            '[data-ts-version-vector-image]'
          );

        if (vectorPreview) {
          vectorPreview.src =
            state.vectorUrl;
        }

        setHidden(
          query(
            card,
            '[data-ts-version-option="vector"]'
          ),
          false
        );

        setInputValue(
          query(
            card,
            '[data-ts-property-vector-url]'
          ),
          state.vectorUrl
        );

        setText(
          buttonText,
          'Enhanced Vector Created'
        );

        selectVersion(
          state,
          'vector'
        );

        window.setTimeout(
          function () {
            setHidden(
              query(
                card,
                '[data-ts-vector-progress]'
              ),
              true
            );
          },
          600
        );
      } catch (error) {
        clearProgressTimer(
          state
        );

        setHidden(
          query(
            card,
            '[data-ts-vector-progress]'
          ),
          true
        );

        var errorBox =
          query(
            card,
            '[data-ts-vector-error]'
          );

        setHidden(
          errorBox,
          false
        );

        setText(
          query(
            card,
            '[data-ts-vector-error-message]'
          ),
          error &&
          error.message
            ? error.message
            : 'Your original artwork remains available.'
        );

        if (button) {
          button.disabled =
            false;
        }

        setText(
          buttonText,
          'Try Enhancement Again'
        );
      }
    }

    function handleLocationChange(state) {
      var card =
        state.card;

      var select =
        query(
          card,
          '[data-ts-location-select]'
        );

      var selectedOption =
        select &&
        select.selectedIndex >= 0
          ? select.options[
              select.selectedIndex
            ]
          : null;

      var maxWidth =
        selectedOption
          ? parseFloat(
              selectedOption.getAttribute(
                'data-max-width'
              )
            )
          : 0;

      var maxHeight =
        selectedOption
          ? parseFloat(
              selectedOption.getAttribute(
                'data-max-height'
              )
            )
          : 0;

      if (
        maxWidth &&
        maxHeight &&
        state.aspectRatio
      ) {
        var width =
          maxWidth;

        var height =
          width /
          state.aspectRatio;

        if (
          height >
          maxHeight
        ) {
          height =
            maxHeight;

          width =
            height *
            state.aspectRatio;
        }

        setInputValue(
          query(
            card,
            '[data-ts-print-width]'
          ),
          roundToTwo(width)
        );

        setInputValue(
          query(
            card,
            '[data-ts-print-height]'
          ),
          roundToTwo(height)
        );
      }

      setInputValue(
        query(
          card,
          '[data-ts-property-location]'
        ),
        select
          ? select.value
          : ''
      );

      updateSizeAndQuality(
        state
      );
    }

    function handleWidthChange(state) {
      var card =
        state.card;

      var widthInput =
        query(
          card,
          '[data-ts-print-width]'
        );

      var heightInput =
        query(
          card,
          '[data-ts-print-height]'
        );

      var width =
        parseFloat(
          widthInput
            ? widthInput.value
            : ''
        );

      if (
        state.isLocked &&
        width > 0 &&
        state.aspectRatio
      ) {
        setInputValue(
          heightInput,
          roundToTwo(
            width /
            state.aspectRatio
          )
        );
      }

      updateSizeAndQuality(
        state
      );
    }

    function handleHeightChange(state) {
      var card =
        state.card;

      var widthInput =
        query(
          card,
          '[data-ts-print-width]'
        );

      var heightInput =
        query(
          card,
          '[data-ts-print-height]'
        );

      var height =
        parseFloat(
          heightInput
            ? heightInput.value
            : ''
        );

      if (
        state.isLocked &&
        height > 0 &&
        state.aspectRatio
      ) {
        setInputValue(
          widthInput,
          roundToTwo(
            height *
            state.aspectRatio
          )
        );
      }

      updateSizeAndQuality(
        state
      );
    }

    function toggleAspectLock(state) {
      state.isLocked =
        !state.isLocked;

      var button =
        query(
          state.card,
          '[data-ts-aspect-lock]'
        );

      if (button) {
        button.classList.toggle(
          'is-locked',
          state.isLocked
        );

        button.setAttribute(
          'aria-pressed',
          state.isLocked
            ? 'true'
            : 'false'
        );

        button.setAttribute(
          'aria-label',
          state.isLocked
            ? 'Unlock artwork proportions'
            : 'Lock artwork proportions'
        );
      }

      setText(
        query(
          state.card,
          '[data-ts-aspect-lock-text]'
        ),
        state.isLocked
          ? 'Locked'
          : 'Unlocked'
      );
    }

    studio.addEventListener(
      'click',
      function (event) {
        var tooltipButton =
          event.target.closest(
            '[data-ts-tooltip-button]'
          );

        if (tooltipButton) {
          event.preventDefault();
          event.stopPropagation();

          toggleTooltip(
            tooltipButton
          );

          return;
        }

        var state =
          getStateFromElement(
            event.target
          );

        if (!state) return;

        var replaceButton =
          event.target.closest(
            '[data-ts-replace-artwork]'
          );

        if (replaceButton) {
          event.preventDefault();

          var fileInput =
            query(
              state.card,
              '[data-ts-artwork-input]'
            );

          if (fileInput) {
            fileInput.value =
              '';

            fileInput.click();
          }

          return;
        }

        var removeButton =
          event.target.closest(
            '[data-ts-remove-artwork]'
          );

        if (removeButton) {
          event.preventDefault();

          clearCardUi(
            state
          );

          return;
        }

        var previewButton =
          event.target.closest(
            '[data-ts-open-preview]'
          );

        if (previewButton) {
          event.preventDefault();

          openModal(
            state.currentPreviewUrl ||
            state.originalUrl,
            state.file
              ? state.file.name
              : 'Artwork Preview'
          );

          return;
        }

        var whiteButton =
          event.target.closest(
            '[data-ts-remove-white]'
          );

        if (whiteButton) {
          event.preventDefault();

          setInputValue(
            query(
              state.card,
              '[data-ts-background-color]'
            ),
            '#ffffff'
          );

          applyBackgroundRemoval(
            state
          );

          return;
        }

        var detectButton =
          event.target.closest(
            '[data-ts-detect-background]'
          );

        if (detectButton) {
          event.preventDefault();

          detectBackgroundColor(
            state
          );

          return;
        }

        var applyButton =
          event.target.closest(
            '[data-ts-apply-background-removal]'
          );

        if (applyButton) {
          event.preventDefault();

          applyBackgroundRemoval(
            state
          );

          return;
        }

        var restoreButton =
          event.target.closest(
            '[data-ts-restore-original]'
          );

        if (restoreButton) {
          event.preventDefault();

          restoreOriginal(
            state
          );

          return;
        }

        var vectorButton =
          event.target.closest(
            '[data-ts-create-vector]'
          );

        if (vectorButton) {
          event.preventDefault();

          createVector(
            state
          );

          return;
        }

        var lockButton =
          event.target.closest(
            '[data-ts-aspect-lock]'
          );

        if (lockButton) {
          event.preventDefault();

          toggleAspectLock(
            state
          );
        }
      }
    );

    studio.addEventListener(
      'change',
      function (event) {
        var state =
          getStateFromElement(
            event.target
          );

        if (!state) return;

        if (
          event.target.matches(
            '[data-ts-artwork-input]'
          )
        ) {
          if (
            event.target.files &&
            event.target.files.length
          ) {
            handleFileSelection(
              state,
              event.target.files[0]
            );
          }

          return;
        }

        if (
          event.target.matches(
            '[data-ts-location-select]'
          )
        ) {
          handleLocationChange(
            state
          );

          return;
        }

        if (
          event.target.matches(
            '[data-ts-version-choice]'
          )
        ) {
          selectVersion(
            state,
            event.target.value
          );
        }
      }
    );

    studio.addEventListener(
      'input',
      function (event) {
        var state =
          getStateFromElement(
            event.target
          );

        if (!state) return;

        if (
          event.target.matches(
            '[data-ts-print-width]'
          )
        ) {
          handleWidthChange(
            state
          );

          return;
        }

        if (
          event.target.matches(
            '[data-ts-print-height]'
          )
        ) {
          handleHeightChange(
            state
          );

          return;
        }

        if (
          event.target.matches(
            '[data-ts-background-tolerance]'
          )
        ) {
          setText(
            query(
              state.card,
              '[data-ts-background-tolerance-value]'
            ),
            event.target.value
          );

          return;
        }

        if (
          event.target.matches(
            '[data-ts-edge-cleanup]'
          )
        ) {
          setText(
            query(
              state.card,
              '[data-ts-edge-cleanup-value]'
            ),
            event.target.value
          );
        }
      }
    );

    document.addEventListener(
      'click',
      function (event) {
        if (
          !event.target.closest(
            '[data-ts-tooltip-button]'
          ) &&
          !event.target.closest(
            '[data-ts-tooltip]'
          )
        ) {
          closeAllTooltips();
        }
      }
    );

    if (modalClose) {
      modalClose.addEventListener(
        'click',
        closeModal
      );
    }

    if (modal) {
      modal.addEventListener(
        'click',
        function (event) {
          if (
            event.target ===
            modal
          ) {
            closeModal();
          }
        }
      );
    }

    document.addEventListener(
      'keydown',
      function (event) {
        if (
          event.key ===
          'Escape'
        ) {
          closeAllTooltips();

          if (
            modal &&
            modal.classList.contains(
              'is-open'
            )
          ) {
            closeModal();
          }
        }
      }
    );

    window.addEventListener(
      'beforeunload',
      function () {
        states.forEach(
          function (state) {
            clearProgressTimer(
              state
            );

            resetStateUrls(
              state
            );
          }
        );
      }
    );

    movePropertiesIntoProductForm(
      studio
    );

    states.forEach(
      function (state) {
        clearProperties(
          state
        );

        selectVersion(
          state,
          'original'
        );
      }
    );

    updateSummary();
  }

  function initializeAllStudios() {
    document
      .querySelectorAll(
        '[data-ts-artwork-studio]'
      )
      .forEach(
        initializeStudio
      );
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initializeAllStudios
    );
  } else {
    initializeAllStudios();
  }

  window.addEventListener(
    'load',
    initializeAllStudios
  );

  document.addEventListener(
    'shopify:section:load',
    initializeAllStudios
  );
})();